import { Typography, Grid, Paper, LinearProgress, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import {
    DataGrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import { ApiResultWrapper, Article, DrillDownParams } from "./CommonInterfaces";
import DoiAnalysisLink from "./DoiAnalysisLink";


function DrillDownTable({ publisherIds, journalIds, startYear, endYear, drillDownTitle, queryRetractions, scitilityRiskScoreLowerBoundary, scitilityRiskScoreHigherBoundary, institutionId, authorId, embed }: DrillDownParams) {

    const [hasInitialData, setHasInitialData] = useState(false)
    const [articles, setArticles] = useState<ApiResultWrapper<Article>>()
    const [isLoading, setIsLoading] = useState(false)
    const [doiAnalysisLoading, setDoiAnalysisLoading] = useState(false)
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ marginTop: 2 }}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarQuickFilter sx={{ paddingLeft: 5 }} />
                <GridToolbarExport
                    slotProps={{
                        tooltip: { title: 'Export data' },
                        button: { variant: 'outlined' },
                    }}
                />
            </GridToolbarContainer>
        );
    }


    function gridColumns(queryRetractions: Boolean): GridColDef<(Article)>[] {
        if (queryRetractions) {
            return [
                {
                    field: 'doi', headerName: 'DOI', flex: 0.4,
                    display: 'flex',
                    renderCell: (e) => {
                        return <DoiAnalysisLink
                            doi={e.value} newRetractions={[]}
                            showDoiInTitle={true}
                            whenLoading={() => setDoiAnalysisLoading(true)}
                            whenLoaded={() => setDoiAnalysisLoading(false)}
                        />
                    }
                },
                { field: 'title', headerName: 'Title', flex: 0.6 },
                { field: 'publicationDate', headerName: 'Publication Date', flex: 0.2 },
                { field: 'retractionDate', headerName: 'Retraction Date', flex: 0.2 },
                { field: 'source', headerName: 'Journal', flex: 0.5 },
                { field: 'publisherName', headerName: 'Publisher', flex: 0.3 }
            ];
        } else {
            return [
                {
                    field: 'doi', headerName: 'DOI', flex: 0.4,
                    display: 'flex',
                    renderCell: (e) => {
                        return <DoiAnalysisLink
                            doi={e.value} newRetractions={[]}
                            showDoiInTitle={true}
                            whenLoading={() => setDoiAnalysisLoading(true)}
                            whenLoaded={() => setDoiAnalysisLoading(false)}
                        />
                    }
                },
                { field: 'title', headerName: 'Title', flex: 0.6 },
                { field: 'publicationDate', headerName: 'Publication Date', flex: 0.2 },
                { field: 'source', headerName: 'Journal', flex: 0.5 },
                { field: 'publisherName', headerName: 'Publisher', flex: 0.3 }
            ];
        }
    }

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true)

            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(argosApiEntryPoint + '/risk-score/filter'
                + '?pageNumber=' + (paginationModel.page + 1)
                + '&pageSize=' + paginationModel.pageSize
                + '&fromPublicationDate=' + startYear + '-01-01T00:00:00.000'
                + '&toPublicationDate=' + endYear + '-12-31T23:59:59.999'
                + (journalIds === undefined ? '' : '&journalOpenAlexIds=' + journalIds)
                + (publisherIds === undefined ? '' : '&publisherOpenAlexIds=' + publisherIds)
                + '&isRetracted=' + queryRetractions
                + '&sortBy=publication-date'
                + (institutionId === undefined ? '' : '&institutionId=' + institutionId)
                + (authorId === undefined ? '' : '&authorId=' + authorId)
                + '&scitilityRiskLowerBound=' + scitilityRiskScoreLowerBoundary
                + (scitilityRiskScoreHigherBoundary === undefined ? '' : '&scitilityRiskHigherBound=' + scitilityRiskScoreHigherBoundary)
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data) => {
                    setIsLoading(false)
                    setArticles(data)
                    setHasInitialData(true)
                })

        }
        fetchData()
    }, [paginationModel]);

    function getRowId(row: Article) {
        return row.doi;
    }

    return <Paper elevation={embed ? 0 : 5} sx={{ marginBottom: embed ? 0 : 5, padding: embed ? 0 : 2 }}>
        {isLoading &&
            <LinearProgress />}
        <Grid container>
            {!embed &&
                <Grid item xs={12} padding={2}>
                    <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h5">
                        {drillDownTitle}
                    </Typography>
                </Grid>
            }
            {hasInitialData &&
                <>
                    <Grid item xs={12}>
                        {doiAnalysisLoading &&
                            <CircularProgress />
                        }
                        {!doiAnalysisLoading &&
                            <DataGrid
                                rows={articles?.data}
                                columns={gridColumns(queryRetractions)}
                                getRowId={getRowId}
                                paginationMode="server"
                                onPaginationModelChange={setPaginationModel}
                                rowCount={articles?.metadata.count ?? 0}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                /*columnHeaderHeight={embed ? 50 : 120}*/
                                autoHeight
                                pageSizeOptions={[10, 20, 50]}
                                slots={{
                                    toolbar: CustomToolbar,
                                }}
                                sx={{
                                    ...(embed && {
                                        fontSize: 10,
                                        width: '100%',          // full width
                                        border: 'none',         // remove outer border
                                        '& .MuiDataGrid-columnHeaders': {
                                            borderBottom: 'none', // optional: remove header bottom border
                                        
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderBottom: 'none', // optional: remove row borders
                                           
                                        },
                                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { display: 'none' },

                                        '& .MuiDataGrid-columnHeader': {
                                           
                                        }
                                    })
                                }}
                            />
                        }
                    </Grid>
                </>
            }
            {!embed &&
                <Grid item xs={12} marginTop={2} paddingRight={2} >
                    <Typography fontFamily='Roboto' color='#003366' align="right" sx={{ fontStyle: 'italic' }}>
                        Powered by Scitility PBC
                    </Typography>
                </Grid>
            }
        </Grid>
    </Paper>
}

export default DrillDownTable